import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { Box, xcss } from '@atlaskit/primitives';
import { useUsers } from '../../../web-client/hooks/useUser';
import { useInlineContext } from '../../../context/inline';
var AKAvatarGroup = /*#__PURE__*/React.lazy(function () {
  return import('@atlaskit/avatar-group');
});
var AKBadge = /*#__PURE__*/React.lazy(function () {
  return import('@atlaskit/badge');
});
export function usersToAKAGData(users, accountIds, intl) {
  // order should match accountIds
  return accountIds.map(function (key) {
    var _ref = users[key] || {},
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? intl.formatMessage(messages.unknownUserMessage) : _ref$name,
      _ref$picture = _ref.picture,
      picture = _ref$picture === void 0 ? '' : _ref$picture;
    return {
      key: key,
      name: name,
      src: picture
    };
  });
}
export function AvatarStack(_ref2) {
  var data = _ref2.data;
  return /*#__PURE__*/React.createElement(AKAvatarGroup, {
    appearance: "stack",
    data: data
  });
}
function More(_ref3) {
  var data = _ref3.data;
  return /*#__PURE__*/React.createElement(AKBadge, null, "+".concat(data.length));
}
export function UserGroupInline(_ref4) {
  var data = _ref4.data;
  var end = Math.max(Math.min(8, data.length), 0); // 4 would be the same as AvatarStack
  var shown = data.slice(0, end);
  var more = data.slice(end);
  var hasMore = more.length > 0;
  return /*#__PURE__*/React.createElement("span", null, shown.map(function (_ref5, i) {
    var name = _ref5.name;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AKBadge, {
      appearance: "primary"
    }, name), hasMore || i !== end - 1 ? ' ' : null);
  }), hasMore ? /*#__PURE__*/React.createElement(More, {
    data: more
  }) : null);
}
// @ts-ignore setting z index to 0 so that the avatars do not overlap other components
var avatarStackWrapper = xcss({
  zIndex: 0,
  position: 'relative'
});
export default function UserGroup(_ref6) {
  var accountIds = _ref6.accountIds,
    client = _ref6.client;
  var users = useUsers(accountIds, client);
  var intl = useIntl();
  var data = usersToAKAGData(users, accountIds, intl);
  var _useInlineContext = useInlineContext(),
    inline = _useInlineContext.inline;
  if (inline) {
    return /*#__PURE__*/React.createElement(UserGroupInline, {
      data: data
    });
  }
  return (
    /*#__PURE__*/
    // Each avatar in an avatar stack has a z index value applied.
    // Setting a container with a z index here to 0 so that the avatars do no overlap other components such as the Select menu
    React.createElement(Box, {
      xcss: avatarStackWrapper
    }, /*#__PURE__*/React.createElement(AvatarStack, {
      data: data
    }))
  );
}
export function UserGroupFn(_ref7) {
  var children = _ref7.children;
  var accountIds = children.map(function (child) {
    if (child.type !== 'User' && child.type !== 'Avatar') {
      return '';
    }
    var props = child.props;
    var _ref8 = props,
      accountId = _ref8.accountId;
    return accountId;
  }).filter(function (x) {
    return x;
  });
  return /*#__PURE__*/React.createElement(UserGroup, {
    accountIds: accountIds,
    client: undefined
  });
}

// New-style component requires ApolloClient as a prop to make a function
// Avatar doesn't use `dispatch` so no upgrading/downgrading required
export function makeUserGroup(_ref9) {
  var client = _ref9.client;
  return function avatarStackNext(_ref10) {
    var children = _ref10.forgeDoc.children;
    var accountIds = children.map(function (child) {
      if (child.type !== 'User' && child.type !== 'Avatar') {
        return '';
      }
      var props = child.props;
      var _ref11 = props,
        accountId = _ref11.accountId;
      return accountId;
    }).filter(function (x) {
      return x;
    });
    return /*#__PURE__*/React.createElement(UserGroup, {
      accountIds: accountIds,
      client: client
    });
  };
}
var messages = defineMessages({
  unknownUserMessage: {
    id: 'confluence.user.group.unknown.user.message',
    defaultMessage: 'Unknown User',
    description: "This is a fallback message that is visible when a user's name is unknown."
  }
});